import { useState } from 'react';

import {
  ActionBar,
  FAIconName,
  InquiryDetailSectionHeader,
  NameWithAvatarCard,
  NameWithAvatarCardProps,
  SkeletonLoader,
  UnconfirmedPatientCard,
} from '@equip.health/ui';

import NoExistingPatientsFoundModal from '~/components/maud/inquiries/InquiryDetail/NoExistingPatientsFoundModal';
import ReviewPatientMatchesModal from '~/components/maud/inquiries/InquiryDetail/ReviewPatientMatchesModal';
import { Section } from '~/components/common/Section';
import { getAllPatientMatches } from '~/lib/util/patient.util';

export interface PatientChartProps {
  id: string;
  isLoading: boolean;
  onCreate: () => void;
  onConfirm: (patientId: string) => void;
  onPatientClick: () => void;
  patientMatchConfirmedAt: string;
  patientMatches: GetPatientMatches;
  patients: NameWithAvatarCardProps[];
}

interface NoPatientsProps {
  id: string;
  onClick: () => void;
}

const NoPatients = ({ id, onClick }: NoPatientsProps) => {
  return (
    <ActionBar
      barIcon={FAIconName.eyeSlashRegular}
      buttonIcon={FAIconName.userPlusRegular}
      buttonText="Create chart"
      id={`${id}-no-patients`}
      onButtonClick={onClick}
      text="No matches found"
      variant="warning"
    />
  );
};

export const PatientChart = ({
  id,
  isLoading,
  onCreate,
  onConfirm,
  onPatientClick,
  patientMatchConfirmedAt,
  patientMatches,
  patients,
}: PatientChartProps) => {
  const [isReviewPatientMatchesModalOpen, setIsReviewPatientMatchesModalOpen] =
    useState<boolean>(false);

  const [
    isNoExistingPatientsFoundModalOpen,
    setIsNoExistingPatientsFoundModalOpen,
  ] = useState<boolean>(false);

  const TOPLEVEL_ID = `${id}__patient-chart`;

  let content: React.ReactNode;

  const combinedPatientMatches = getAllPatientMatches(patientMatches);

  if (patientMatchConfirmedAt && patients.length === 1) {
    content = (
      <NameWithAvatarCard
        {...patients[0]}
        id={TOPLEVEL_ID}
        onClick={onPatientClick}
      />
    );
  } else if (combinedPatientMatches.length == 0) {
    content = (
      <NoPatients
        id={TOPLEVEL_ID}
        onClick={() => setIsNoExistingPatientsFoundModalOpen(true)}
      />
    );
  } else {
    content = (
      <UnconfirmedPatientCard
        count={combinedPatientMatches.length}
        id={TOPLEVEL_ID}
        reviewAction={() => setIsReviewPatientMatchesModalOpen(true)}
      />
    );
  }

  const handleOnCreatePatient = () => {
    setIsNoExistingPatientsFoundModalOpen(false);
    onCreate();
  };

  return (
    <Section id={TOPLEVEL_ID}>
      <InquiryDetailSectionHeader
        id={`${TOPLEVEL_ID}__header`}
        title="Patient chart"
      />
      <SkeletonLoader id="inquiry__patient-chart" isLoaded={!isLoading}>
        {content}
      </SkeletonLoader>
      <ReviewPatientMatchesModal
        id={`${TOPLEVEL_ID}__review-patient-matches-modal`}
        isOpen={isReviewPatientMatchesModalOpen}
        onCancel={() => setIsReviewPatientMatchesModalOpen(false)}
        onConfirmPatient={onConfirm}
        onCreatePatient={onCreate}
        patientMatches={combinedPatientMatches}
      />
      <NoExistingPatientsFoundModal
        id={`${TOPLEVEL_ID}__no-existing-patients-found-modal`}
        isOpen={isNoExistingPatientsFoundModalOpen}
        onCancel={() => setIsNoExistingPatientsFoundModalOpen(false)}
        onCreateNewProfile={handleOnCreatePatient}
      />
    </Section>
  );
};
